// LeadFilterUtils.ts - Extract constants and utilities with minimal changes

import { DatePickerProps } from "antd/es/date-picker";
import moment from "moment";

// Common types
export interface DateRange {
  startDate?: string;
  endDate?: string;
}

// Constants - direct extraction from LeadFilter
export const sortLeadBy = [
  { value: "createdDate", label: "Created Date" },
  { value: "name", label: "Name" },
  { value: "saleValue", label: "Sale Value" },
  { value: "followupDate", label: "Followup Date" },
  { value: "activityDate", label: "Activity Date" },
];

export const dateLeadAdded = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "3DayBefore", label: "3 Days Before Now" },
  { value: "1WeekBefore", label: "1 Week Before Now" },
  { value: "1MonthBefore", label: "1 Month Before Now" },
  { value: "custom", label: "Custom Date" },
];

export const operatorOptions = [
  { value: "eq", label: "Equals" },
  { value: "gt", label: "Greater than" },
  { value: "lt", label: "Less than" }
];

// Define filter categories with their icons and related filters
export const filterCategories = [
  {
    id: "basic",
    label: "Basic Filters",
    icon: "Layers",
    defaultOpen: true,
    filters: ["sortLeadBy", "dateLead", "label", "status", "sources"]
  },
  {
    id: "team",
    label: "Team & Assignment",
    icon: "Users",
    defaultOpen: false,
    filters: ["team", "employee", "assignedLead"]
  },
  {
    id: "activity",
    label: "Call & Activity",
    icon: "Activity",
    defaultOpen: false,
    filters: ["durationOperator", "callCountOperator", "callDurationValue", "callCountValue", "customActivityName", "activitySelectedDate"]
  },
  {
    id: "task",
    label: "Tasks & Custom Fields",
    icon: "Settings",
    defaultOpen: false,
    filters: ["taskType", "taskSelectedDate", "selectCustomLead", "operator", "leadformValue"]
  }
,
  {
    id: "products", // NEW 5th SECTION
    label: "Products",
    icon: "Package",
    defaultOpen: false,
    filters: ["products"]
  }
];

// Utility functions - using moment UTC for consistent timezone handling
export const getDateRange = (
  dateType: string
): { startDate?: string; endDate?: string } => {
  const now = moment.utc();

  switch (dateType) {
    case "today":
      return {
        startDate: now.clone().startOf('day').format(),
        endDate: now.clone().endOf('day').format(),
      };
    case "yesterday":
      const yesterday = now.clone().subtract(1, 'day');
      return {
        startDate: yesterday.startOf('day').format(),
        endDate: yesterday.endOf('day').format(),
      };
    case "3DayBefore":
      return {
        startDate: now.clone().subtract(3, 'days').startOf('day').format(),
        endDate: now.clone().endOf('day').format(),
      };
    case "1WeekBefore":
      return {
        startDate: now.clone().subtract(7, 'days').startOf('day').format(),
        endDate: now.clone().endOf('day').format(),
      };
    case "1MonthBefore":
      return {
        startDate: now.clone().subtract(1, 'month').startOf('day').format(),
        endDate: now.clone().endOf('day').format(),
      };
    default:
      return {};
  }
};

export const convertDateRangeToLabel = (startDate: string, endDate: string) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const threeDaysBefore = new Date(today);
  threeDaysBefore.setDate(threeDaysBefore.getDate() - 3);
  const oneWeekBefore = new Date(today);
  oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);
  const oneMonthBefore = new Date(today);
  oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);

  const startDateObj = new Date(startDate);
  startDateObj.setHours(0, 0, 0, 0);
  const endDateObj = new Date(endDate);
  endDateObj.setHours(0, 0, 0, 0);
  yesterday.setHours(0, 0, 0, 0);
  threeDaysBefore.setHours(0, 0, 0, 0);
  oneWeekBefore.setHours(0, 0, 0, 0);
  oneMonthBefore.setHours(0, 0, 0, 0);

  if (
    startDateObj.toDateString() === today.toDateString() &&
    endDateObj.toDateString() === today.toDateString()
  ) {
    return dateLeadAdded.find((item) => item.value === "today");
  } else if (startDateObj >= yesterday && endDateObj <= today) {
    return dateLeadAdded.find((item) => item.value === "yesterday");
  } else if (startDateObj >= threeDaysBefore && endDateObj <= today) {
    return dateLeadAdded.find((item) => item.value === "3DayBefore");
  } else if (startDateObj >= oneWeekBefore && endDateObj <= today) {
    return dateLeadAdded.find((item) => item.value === "1WeekBefore");
  } else if (startDateObj >= oneMonthBefore && endDateObj <= today) {
    return dateLeadAdded.find((item) => item.value === "1MonthBefore");
  } else {
    return dateLeadAdded.find((item) => item.value === "custom");
  }
};

// Export the icon components by name to simplify imports in other files
export const iconsByName = {
  Layers: "Layers",
  Users: "Users",
  Activity: "Activity",
    Package: "Package",
  Settings: "Settings",
  ChevronDown: "ChevronDown",
  ChevronRight: "ChevronRight"
};